/**
 * CreativesList Component
 * Список креативов с таблицей
 */

import React, { useState, useEffect } from 'react';
import { useCreatives } from '../../hooks/useCreatives';
import api from '../../services/api';
import './CreativesList.css';
import ViewCreativeModal from './ViewCreativeModal';
import EditCreativeModal from './EditCreativeModal';
import CreateCreativeModal from "../CreateCreativeModal";
import SyncButton from '../SyncButton';
import { EditContractForm } from '../ContractsPage';

const CreativesList = () => {
  const { creatives, loading, error, syncCreatives } = useCreatives();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCreatives, setSelectedCreatives] = useState([]);
  const [creativeTypes, setCreativeTypes] = useState([]);
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractModalMode, setContractModalMode] = useState("edit");
  const [contracts, setContracts] = useState([]);
  const [dictionariesLoading, setDictionariesLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [viewsStats, setViewsStats] = useState({}); // Статистика просмотров
  const [sortBy, setSortBy] = useState('date'); // 'date', 'status'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  useEffect(() => {
    // Параллельная загрузка справочников для ускорения
    setDictionariesLoading(true);
    Promise.all([
      api.get("/dictionaries/creative-types"),
    api.get("/contracts/")
    ])
      .then(([typesRes, contractsRes]) => {
        setCreativeTypes(typesRes.data.items || []);
        setContracts(contractsRes.data || []);
      })
      .catch(err => console.error("Error loading dictionaries:", err))
      .finally(() => setDictionariesLoading(false));
  }, []);

  // Извлекаем статистику просмотров из данных креативов
  useEffect(() => {
    if (creatives && creatives.length > 0) {
      const stats = {};
      creatives.forEach(creative => {
        // Добавляем все креативы в stats, даже с 0 просмотрами
        if (creative.total_views !== undefined) {
          stats[creative.erid] = creative.total_views;
        }
      });
      setViewsStats(stats);
    }
  }, [creatives]);

  const handleUpdate = async (erid, formData) => {
    try {
      // Подготавливаем данные для отправки (все поля креатива)
      const payload = {
        name: formData.name,
        description: formData.description,
        brand: formData.brand,
        category: formData.category,
        form: formData.form, // Уже нормализовано в EditCreativeModal (с подчеркиваниями)
        targeting: formData.target_audience,
        texts: formData.creative_text ? [formData.creative_text] : [],
        target_urls: Array.isArray(formData.creative_urls) ? formData.creative_urls : [],
        kktus: formData.kktu_code ? [formData.kktu_code] : [],
        contract_external_id: formData.contract_external_id,
        telegram_post_url: formData.telegram_post_url,
        vk_post_url: formData.vk_post_url
      };

      await api.put(`/creatives/${erid}`, payload);

      alert('Креатив успешно обновлен');
      setShowEditModal(false);
      window.location.reload(); // Перезагружаем для обновления списка
    } catch (err) {
      alert(`Ошибка: ${err.message}`);
    }
  };

  const handleSync = async () => {
    try {
      const result = await syncCreatives();
      alert(`Синхронизировано: ${result.synced} из ${result.total}`);
    } catch (err) {
      alert(`Ошибка синхронизации: ${err.message}`);
    }
  };

  const handleView = (creative) => {
    setSelectedCreative(creative);
    setShowViewModal(true);
  };

  const handleEdit = (creative) => {
    setSelectedCreative(creative);
    setShowEditModal(true);
  };

  const toggleSelectAll = () => {
    if (selectedCreatives.length === creatives.length) {
      setSelectedCreatives([]);
    } else {
      setSelectedCreatives(creatives.map(c => c.erid));
    }
  };

  const toggleSelect = (erid) => {
    if (selectedCreatives.includes(erid)) {
      setSelectedCreatives(selectedCreatives.filter(e => e !== erid));
    } else {
      setSelectedCreatives([...selectedCreatives, erid]);
    }
  };

  const handleContractClick = async (contractId) => {
    const contract = contracts.find(c => c.external_id === contractId);
    if (!contract) {
      alert('⚠️ Договор не найден в локальном списке. Обновите страницу и попробуйте снова.');
      return;
    }

    try {
      // Загружаем актуальные данные из VK ОРД
      const vkResponse = await api.get(`/contracts/vk-ord/${contract.external_id}`);
      const vkData = vkResponse.data;

      const updatedData = {
        type: vkData.type,
        client_external_id: vkData.client_external_id,
        contractor_external_id: vkData.contractor_external_id,
        date: vkData.date,
        serial: vkData.serial,
        subject_type: vkData.subject_type,
        action_type: vkData.action_type,
        amount: vkData.amount,
        flags: vkData.flags || [],
        parent_contract_external_id: vkData.parent_contract_external_id,
        comment: vkData.comment,
        end_date: vkData.date_end
      };

      // Обновляем локальную БД
      await api.put(`/contracts/detail-ext/${contract.external_id}`, updatedData);

      // Обновляем локальный список договоров
      const updatedContracts = await api.get('/contracts/');
      setContracts(updatedContracts.data || []);

      setSelectedContract({ ...contract, ...vkData });
    } catch (error) {
      console.error('Ошибка загрузки договора из VK ОРД:', error);
      setSelectedContract(contract);
    }

    setContractModalMode('edit');
    setShowContractModal(true);
  };

  const handleSaveContract = async (contractData) => {
    if (!selectedContract) return;
    try {
      await api.put(`/contracts/detail-ext/${selectedContract.external_id}`, contractData);
      alert('✅ Договор обновлен');
      setShowContractModal(false);

      const updatedContracts = await api.get('/contracts/');
      setContracts(updatedContracts.data || []);
    } catch (error) {
      alert(`❌ Ошибка: ${error.message}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    // Проверяем что дата валидна
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const getFormName = (formCode) => {
    if (!formCode) return "—";
    // Если справочники еще не загружены, не возвращаем сырое значение
    if (dictionariesLoading || creativeTypes.length === 0) {
      return null;
    }
    // Ищем напрямую по code без нормализации, так как API возвращает с подчеркиваниями
    const type = creativeTypes.find(t => t.code === formCode);
    return type ? type.name : formCode;
  };

  const getFormDescription = (formCode) => {
    if (!formCode) return null;
    if (dictionariesLoading || creativeTypes.length === 0) {
      return null;
    }
    const type = creativeTypes.find(t => t.code === formCode);
    return type?.description || null;
  };

  const getContractInfo = (contractId) => {
    if (!contractId) return "—";
    const contract = contracts.find(c => c.external_id === contractId);
    if (!contract) {
      // Если контракты еще не загружены, показываем пустую строку вместо ID
      return contracts.length === 0 ? "" : "—";
    }
    return (
      <a
        href="#"
        onClick={(e) => { e.preventDefault(); handleContractClick(contractId); }}
        style={{ color: "#0969da", textDecoration: "none", cursor: "pointer" }}
      >
        {contract.serial || contractId}
      </a>
    );
  };

  const getErirStatusBadge = (creative) => {
    const state = creative?.erir_status_state;
    const updatedAt = creative?.erir_status_updated_at;
    const payload = creative?.erir_status_payload;

    if (!state) {
      return <span className="status-badge unknown" title="Статус ЕРИР не известен">Неизвестно</span>;
    }

    const statusMap = {
      'verified': { label: 'Принято в ЕРИР', className: 'approved' },
      'accepted': { label: 'Принято в ЕРИР', className: 'approved' },
      'registered': { label: 'Принято в ЕРИР', className: 'approved' },
      'processing': { label: 'Обрабатывается', className: 'pending' },
      'pending': { label: 'Отправлено', className: 'pending' },
      'submitted': { label: 'Отправлено', className: 'pending' },
      'sent': { label: 'Отправлено', className: 'pending' },
      'waiting': { label: 'Ожидает документы', className: 'pending' },
      'waiting_documents': { label: 'Ожидает документы', className: 'pending' },
      'rejected': { label: 'Отклонено ЕРИР', className: 'rejected' },
      'declined': { label: 'Отклонено ЕРИР', className: 'rejected' },
      'error': { label: 'Ошибка', className: 'rejected' },
      'failed': { label: 'Ошибка', className: 'rejected' },
      'not_found': { label: 'Не найдено в ЕРИР', className: 'unknown' }
    };

    const statusInfo = statusMap[state.toLowerCase()] || { label: state, className: 'unknown' };
    
    const tooltipParts = [`Состояние: ${state}`];
    if (updatedAt) {
      const date = new Date(updatedAt);
      tooltipParts.push(`Обновлено: ${date.toLocaleDateString('ru-RU')} ${date.toLocaleTimeString('ru-RU')}`);
    }

    return (
      <span 
        className={`status-badge ${statusInfo.className}`}
        title={tooltipParts.join('\n')}
      >
        {statusInfo.label}
      </span>
    );
  };

  // Фильтрация креативов по поисковому запросу
  const filteredCreatives = creatives.filter(creative => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      creative.name?.toLowerCase().includes(query) ||
      creative.erid?.toLowerCase().includes(query) ||
      getFormName(creative.form)?.toLowerCase().includes(query) ||
      getContractInfo(creative.contract_external_id)?.toString().toLowerCase().includes(query)
    );
  });

  // Сортировка креативов
  const sortedCreatives = [...filteredCreatives].sort((a, b) => {
    if (sortBy === 'date') {
      // Креативы БЕЗ даты всегда в конце (Date(0) = 1970-01-01)
      const dateA = a.create_date ? new Date(a.create_date) : new Date(0);
      const dateB = b.create_date ? new Date(b.create_date) : new Date(0);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    } else if (sortBy === 'status') {
      const statusA = a.erir_status_state || '';
      const statusB = b.erir_status_state || '';
      return sortOrder === 'desc' 
        ? statusB.localeCompare(statusA)
        : statusA.localeCompare(statusB);
    }
    return 0;
  });

  // Функция для переключения сортировки
  const handleSort = (column) => {
    if (sortBy === column) {
      // Переключаем порядок если та же колонка
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      // Новая колонка - сортируем по убыванию
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Пагинация для отфильтрованных и отсортированных креативов
  const totalPages = Math.ceil(sortedCreatives.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentCreatives = sortedCreatives.slice(startIndex, endIndex);

  // Показываем спиннер загрузки пока идут запросы
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Загрузка креативов...</p>
      </div>
    );
  }

  // Обработка ошибок
  if (error) {
    return (
      <div className="error-container">
        <h3>❌ Ошибка загрузки</h3>
        <p>{error}</p>
        <button className="retry-btn" onClick={() => window.location.reload()}>
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div className="creatives-list">
      <div className="table-container">
        {/* Поиск сверху */}
        <div className="table-controls">
          <div className="search-bar">
            <input
              type="text"
              placeholder="🔍 Поиск по креативам..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); // Сброс на первую страницу при поиске
              }}
              className="search-input"
            />
            {searchQuery && (
              <button 
                className="clear-search"
                onClick={() => setSearchQuery('')}
              >
                ×
              </button>
            )}
          </div>
          <div className="table-options">
            <button 
              className="add-button"
              onClick={() => setShowCreateModal(true)}
            >
              ➕ Добавить
            </button>
            <SyncButton 
              customHandler={handleSync}
              tooltipText="Синхронизировать креативы с VK ORД"
            />
          </div>
        </div>

        {/* Предупреждение о креативах без даты */}
        {(() => {
          const creativesWithoutDate = creatives.filter(c => !c.create_date);
          return creativesWithoutDate.length > 0 && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <strong style={{ color: '#856404', fontSize: '14px' }}>
                  Найдено {creativesWithoutDate.length} {creativesWithoutDate.length === 1 ? 'креатив' : 'креативов'} без даты создания
                </strong>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#856404' }}>
                  Проверьте: {creativesWithoutDate.map(c => c.name).slice(0, 3).join(', ')}
                  {creativesWithoutDate.length > 3 && ` и ещё ${creativesWithoutDate.length - 3}`}
                </p>
              </div>
            </div>
          );
        })()}

        {filteredCreatives.length === 0 ? (
          <div className="empty-table">
            {searchQuery ? (
              <>
                <span>🔍</span>
                <h3>Ничего не найдено</h3>
                <p>Попробуйте изменить поисковый запрос</p>
                <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                  Сбросить поиск
                </button>
              </>
            ) : (
              <>
                <span>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 9H21" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 21V9" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="5" r="1" fill="#9CA3AF"/>
                    <circle cx="16" cy="5" r="1" fill="#9CA3AF"/>
                    <rect x="6" y="12" width="2" height="6" rx="0.5" fill="#E5E7EB"/>
                    <rect x="12" y="14" width="2" height="4" rx="0.5" fill="#E5E7EB"/>
                    <rect x="16" y="11" width="2" height="7" rx="0.5" fill="#E5E7EB"/>
                  </svg>
                </span>
                <h3>Нет креативов</h3>
                <p>Нажмите "Добавить публикацию" чтобы создать первый креатив</p>
                <SyncButton 
                  customHandler={handleSync}
                  tooltipText="Синхронизировать креативы с VK ORД"
                />
              </>
            )}
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'center' }}>Название / ERID</th>
                  <th style={{ textAlign: 'center' }}>Форма</th>
                  <th 
                    style={{ width: '140px', cursor: 'pointer', userSelect: 'none', textAlign: 'center' }}
                    onClick={() => handleSort('date')}
                    title="Нажмите для сортировки"
                  >
                    Дата создания{' '}
                    <span className="sort-icon">
                      {sortBy === 'date' ? (sortOrder === 'desc' ? '↓' : '↑') : '⇅'}
                    </span>
                  </th>
                  <th style={{ textAlign: 'center' }}>Договор</th>
                  <th style={{ width: '100px', textAlign: 'center' }} title="Просмотры">👁️</th>
                  <th 
                    style={{ width: '100px', cursor: 'pointer', userSelect: 'none', textAlign: 'center' }}
                    onClick={() => handleSort('status')}
                    title="Нажмите для сортировки"
                  >
                    Статус{' '}
                    <span className="sort-icon">
                      {sortBy === 'status' ? (sortOrder === 'desc' ? '↓' : '↑') : '⇅'}
                    </span>
                  </th>
                  <th style={{ width: '120px', textAlign: 'right', paddingRight: '16px' }}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {currentCreatives.map(creative => (
                  <tr key={creative.erid}>
                    <td className="creative-name" style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 500 }}>{creative.name}</div>
                      <div style={{ fontSize: '12px', color: '#656d76', marginTop: '4px' }}>{creative.erid}</div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {(() => {
                        const formName = getFormName(creative.form);
                        const formDescription = getFormDescription(creative.form);
                        if (formName === null) {
                          return <span className="form-badge" style={{ opacity: 0.5 }}>...</span>;
                        }
                        return (
                          <span 
                            className="form-badge" 
                            title={formDescription || formName}
                            style={{ cursor: formDescription ? 'help' : 'default' }}
                          >
                            {formName}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {creative.create_date ? (
                        formatDate(creative.create_date)
                      ) : (
                        <span style={{ color: '#d73a49', fontSize: '12px', fontWeight: '500' }} title="Отсутствует create_date">
                          ⚠️ Нет даты
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>{getContractInfo(creative.contract_external_id)}</td>
                    <td style={{ textAlign: 'center', fontWeight: '500', color: '#333' }}>
                      {viewsStats[creative.erid] !== undefined && viewsStats[creative.erid] > 0 ? (
                        <span style={{ color: '#0969da', fontWeight: '600' }}>
                          {viewsStats[creative.erid].toLocaleString()}
                        </span>
                      ) : (
                        <span style={{ color: '#999' }}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>{getErirStatusBadge(creative)}</td>
                    <td style={{ textAlign: 'right', paddingRight: '16px' }}>
                      <div className="action-buttons" style={{ display: 'inline-flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button className="icon-btn" onClick={() => handleEdit(creative)} title="Редактировать">
                          ✏️
                        </button>
                        <button className="icon-btn" title="Копировать">
                          📋
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Пагинация внизу */}
            {totalPages > 1 && (
              <div className="pagination-bottom">
                <div className="pagination-info">
                  Показано {startIndex + 1}-{Math.min(endIndex, sortedCreatives.length)} из {sortedCreatives.length}
                </div>
                <div className="pagination-controls">
                  <button 
                    className="page-btn" 
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    ←
                  </button>
                  <button className="page-btn active">{currentPage}</button>
                  {currentPage < totalPages && (
                    <button className="page-btn" onClick={() => setCurrentPage(currentPage + 1)}>
                      {currentPage + 1}
                    </button>
                  )}
                  {currentPage + 1 < totalPages && (
                    <button className="page-btn" onClick={() => setCurrentPage(currentPage + 2)}>
                      {currentPage + 2}
                    </button>
                  )}
                  <button 
                    className="page-btn" 
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    →
                  </button>
                  <select 
                    className="rows-select"
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={20}>20 / стр.</option>
                    <option value={50}>50 / стр.</option>
                    <option value={100}>100 / стр.</option>
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Модальные окна */}
      <ViewCreativeModal
        creative={selectedCreative}
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        getFormName={getFormName}
        getContractInfo={getContractInfo}
      />
      
      <EditCreativeModal
        creative={selectedCreative}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          // Обновляем список креативов после закрытия модалки (чтобы увидеть обновлённый статус ЕРИР)
          window.location.reload();
        }}
        onUpdate={handleUpdate}
      />

      <CreateCreativeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => {
          setShowCreateModal(false);
          window.location.reload();
        }}
      />

      {/* Модальное окно договора */}
      {showContractModal && (
        <div className="modal-overlay" onClick={() => setShowContractModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {contractModalMode === 'view'
                  ? '📋 Просмотр договора'
                  : contractModalMode === 'add'
                    ? '➕ Новый договор'
                    : '✏️ Редактирование договора'}
              </h2>
              <button className="modal-close" onClick={() => setShowContractModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <EditContractForm
                contract={selectedContract}
                mode={contractModalMode}
                onSave={handleSaveContract}
                onCancel={() => setShowContractModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativesList;
